// File: src/lib/rbac/roles.ts
// Purpose: Role-Based Access Control Configuration for VIBE Platform
// VIBE - RBAC System

export enum UserRole {
  DEVELOPER = 'developer',
  TEAM_LEAD = 'team_lead',
  PROJECT_MANAGER = 'project_manager',
  QA_REVIEWER = 'qa_reviewer',
  DEPARTMENT_HEAD = 'department_head',
  SUPER_ADMIN = 'super_admin',
  ADMIN= 'admin',
}

export enum Permission {
  CONFIGURE_INTEGRATIONS = 'configure_integrations',
  CONFIGURE_AUTOMATION= 'configure_automation',
  APPROVE_APIS= 'approve_apis',
  APPROVE_ASSIGNMENTS = 'approve_assignments',
  VIEW_ALL_TEAMS = 'view_all_teams',

  // Dashboard Permissions
  VIEW_PERSONAL_DASHBOARD = 'view_personal_dashboard',
  VIEW_TEAM_DASHBOARD = 'view_team_dashboard',
  VIEW_PROJECT_DASHBOARD = 'view_project_dashboard',
  VIEW_DEPARTMENT_DASHBOARD = 'view_department_dashboard',
  VIEW_PLATFORM_DASHBOARD = 'view_platform_dashboard',
  
  // AI Assistant Permissions
  USE_AI_CHAT = 'use_ai_chat',
  USE_CODE_MODE = 'use_code_mode',
  USE_ASK_MODE = 'use_ask_mode',
  USE_DEBUG_MODE = 'use_debug_mode',
  USE_ARCHITECT_MODE = 'use_architect_mode',
  USE_ORCHESTRATOR_MODE = 'use_orchestrator_mode',
  USE_REVIEW_MODE = 'use_review_mode',
  USE_COMPLIANCE_MODE = 'use_compliance_mode',
  CREATE_CUSTOM_MODES = 'create_custom_modes',
  
  // Code Development Permissions
  GENERATE_CODE = 'generate_code',
  MODIFY_OWN_CODE = 'modify_own_code',
  MODIFY_TEAM_CODE = 'modify_team_code',
  MODIFY_ANY_CODE = 'modify_any_code',
  CREATE_PROJECTS = 'create_projects',
  DELETE_PROJECTS = 'delete_projects',
  APPROVE_ARCHITECTURE = 'approve_architecture',
  
  // Testing Permissions
  RUN_TESTS = 'run_tests',
  CREATE_TESTS = 'create_tests',
  MODIFY_TEST_CONFIG = 'modify_test_config',
  VIEW_DIAGNOSTICS = 'view_diagnostics',
  CONFIGURE_QUALITY_GATES = 'configure_quality_gates',
  
  // Task Management Permissions
  VIEW_OWN_TASKS = 'view_own_tasks',
  VIEW_TEAM_TASKS = 'view_team_tasks',
  VIEW_ALL_TASKS = 'view_all_tasks',
  ASSIGN_TASKS = 'assign_tasks',
  CREATE_TASKS = 'create_tasks',
  MANAGE_SPRINTS = 'manage_sprints',
  MANAGE_PORTFOLIO = 'manage_portfolio',
  
  // Version Control Permissions
  COMMIT_CODE = 'commit_code',
  CREATE_BRANCHES = 'create_branches',
  DELETE_BRANCHES = 'delete_branches',
  MERGE_CODE = 'merge_code',
  APPROVE_MERGES = 'approve_merges',
  CREATE_RELEASES = 'create_releases',
  
  // API Integration Permissions
  USE_APIS = 'use_apis',
  CONFIGURE_APIS = 'configure_apis',
  ADD_INTEGRATIONS = 'add_integrations',
  MANAGE_VENDORS = 'manage_vendors',
  
  // Deployment Permissions
  SUBMIT_FOR_REVIEW = 'submit_for_review',
  DEPLOY_TO_DEV = 'deploy_to_dev',
  DEPLOY_TO_TEST = 'deploy_to_test',
  DEPLOY_TO_PRODUCTION = 'deploy_to_production',
  MANAGE_INFRASTRUCTURE = 'manage_infrastructure',
  
  // Compliance Permissions
  VIEW_COMPLIANCE = 'view_compliance',
  ENFORCE_COMPLIANCE = 'enforce_compliance',
  MODIFY_COMPLIANCE = 'modify_compliance',
  AUDIT_ACCESS = 'audit_access',
  GENERATE_REPORTS = 'generate_reports',
  
  // Analytics Permissions
  VIEW_OWN_ANALYTICS = 'view_own_analytics',
  VIEW_TEAM_ANALYTICS = 'view_team_analytics',
  VIEW_PROJECT_ANALYTICS = 'view_project_analytics',
  VIEW_DEPARTMENT_ANALYTICS = 'view_department_analytics',
  VIEW_PLATFORM_ANALYTICS = 'view_platform_analytics',
  
  // Resource Management Permissions
  USE_TEMPLATES = 'use_templates',
  CREATE_TEMPLATES = 'create_templates',
  MANAGE_TRAINING = 'manage_training',
  DEFINE_STANDARDS = 'define_standards',
  
  // System Administration Permissions
  MANAGE_USERS = 'manage_users',
  MANAGE_ROLES = 'manage_roles',
  MANAGE_PLATFORM = 'manage_platform',
  VIEW_SYSTEM_HEALTH = 'view_system_health',
  MANAGE_BILLING = 'manage_billing'
}

// Define base developer permissions
const developerPermissions: Permission[] = [
  // Dashboard
  Permission.VIEW_PERSONAL_DASHBOARD,
  
  // AI Assistant
  Permission.USE_AI_CHAT,
  Permission.USE_CODE_MODE,
  Permission.USE_ASK_MODE,
  Permission.USE_DEBUG_MODE,
  
  // Code Development
  Permission.GENERATE_CODE,
  Permission.MODIFY_OWN_CODE,
  
  // Testing
  Permission.RUN_TESTS,
  Permission.VIEW_DIAGNOSTICS,
  
  // Task Management
  Permission.VIEW_OWN_TASKS,
  Permission.CREATE_TASKS,
  
  // Version Control
  Permission.COMMIT_CODE,
  Permission.CREATE_BRANCHES,
  
  // API Integration
  Permission.USE_APIS,
  
  // Deployment
  Permission.SUBMIT_FOR_REVIEW,
  
  // Compliance
  Permission.VIEW_COMPLIANCE,
  
  // Analytics
  Permission.VIEW_OWN_ANALYTICS,
  
  // Resources
  Permission.USE_TEMPLATES
]

// Role-Permission Mapping
export const rolePermissions: Record<UserRole, Permission[]> = {
  [UserRole.DEVELOPER]: developerPermissions,
  
  [UserRole.TEAM_LEAD]: [
    // All Developer permissions
    ...developerPermissions,
    
    // Additional Dashboard
    Permission.VIEW_TEAM_DASHBOARD,
    
    // Additional AI Modes
    Permission.USE_ARCHITECT_MODE,
    Permission.USE_REVIEW_MODE,
    
    // Additional Code Development
    Permission.MODIFY_TEAM_CODE,
    
    // Additional Testing
    Permission.CREATE_TESTS,
    Permission.CONFIGURE_QUALITY_GATES,
    
    // Additional Task Management
    Permission.VIEW_TEAM_TASKS,
    Permission.ASSIGN_TASKS,
    Permission.MANAGE_SPRINTS,
    
    // Additional Version Control
    Permission.APPROVE_MERGES,
    
    // Additional API Integration
    Permission.CONFIGURE_APIS,
    
    // Additional Deployment
    Permission.DEPLOY_TO_DEV,
    Permission.DEPLOY_TO_TEST,
    
    // Additional Compliance
    Permission.ENFORCE_COMPLIANCE,
    
    // Additional Analytics
    Permission.VIEW_TEAM_ANALYTICS,
    
    // Additional Resources
    Permission.CREATE_TEMPLATES,
    Permission.DEFINE_STANDARDS
  ],
  
  [UserRole.PROJECT_MANAGER]: [
    // Dashboard
    Permission.VIEW_PERSONAL_DASHBOARD,
    Permission.VIEW_PROJECT_DASHBOARD,
    
    // AI Assistant
    Permission.USE_AI_CHAT,
    Permission.USE_ORCHESTRATOR_MODE,
    Permission.CREATE_CUSTOM_MODES,
    
    // Code Development
    Permission.CREATE_PROJECTS,
    Permission.APPROVE_ARCHITECTURE,
    
    // Testing
    Permission.VIEW_DIAGNOSTICS,
    Permission.CONFIGURE_QUALITY_GATES,
    
    // Task Management
    Permission.VIEW_ALL_TASKS,
    Permission.ASSIGN_TASKS,
    Permission.CREATE_TASKS,
    Permission.MANAGE_SPRINTS,
    Permission.MANAGE_PORTFOLIO,
    
    // Version Control
    Permission.CREATE_RELEASES,
    
    // API Integration
    Permission.ADD_INTEGRATIONS,
    Permission.MANAGE_VENDORS,
    
    // Deployment
    Permission.DEPLOY_TO_PRODUCTION,
    
    // Compliance
    Permission.VIEW_COMPLIANCE,
    Permission.ENFORCE_COMPLIANCE,
    Permission.GENERATE_REPORTS,
    
    // Analytics
    Permission.VIEW_PROJECT_ANALYTICS,
    
    // Resources
    Permission.CREATE_TEMPLATES,
    Permission.DEFINE_STANDARDS,
    Permission.MANAGE_TRAINING
  ],
  
  [UserRole.QA_REVIEWER]: [
    // Dashboard
    Permission.VIEW_PERSONAL_DASHBOARD,
    
    // AI Assistant
    Permission.USE_AI_CHAT,
    Permission.USE_REVIEW_MODE,
    Permission.USE_DEBUG_MODE,
    Permission.USE_COMPLIANCE_MODE,
    
    // Testing
    Permission.RUN_TESTS,
    Permission.CREATE_TESTS,
    Permission.MODIFY_TEST_CONFIG,
    Permission.VIEW_DIAGNOSTICS,
    Permission.CONFIGURE_QUALITY_GATES,
    
    // Task Management
    Permission.VIEW_ALL_TASKS,
    Permission.CREATE_TASKS,
    
    // Version Control
    Permission.APPROVE_MERGES,
    
    // API Integration
    Permission.USE_APIS,
    
    // Compliance
    Permission.VIEW_COMPLIANCE,
    Permission.ENFORCE_COMPLIANCE,
    Permission.AUDIT_ACCESS,
    Permission.GENERATE_REPORTS,
    
    // Analytics
    Permission.VIEW_OWN_ANALYTICS,
    Permission.VIEW_PROJECT_ANALYTICS,
    
    // Resources
    Permission.USE_TEMPLATES,
    Permission.CREATE_TEMPLATES
  ],
  
  [UserRole.DEPARTMENT_HEAD]: [
    // Dashboard
    Permission.VIEW_DEPARTMENT_DASHBOARD,
    Permission.VIEW_PROJECT_DASHBOARD,
    Permission.VIEW_TEAM_DASHBOARD,
    
    // AI Assistant
    Permission.USE_AI_CHAT,
    Permission.USE_ORCHESTRATOR_MODE,
    Permission.CREATE_CUSTOM_MODES,
    
    // Code Development
    Permission.CREATE_PROJECTS,
    Permission.DELETE_PROJECTS,
    Permission.APPROVE_ARCHITECTURE,
    
    // Testing
    Permission.VIEW_DIAGNOSTICS,
    Permission.CONFIGURE_QUALITY_GATES,
    
    // Task Management
    Permission.VIEW_ALL_TASKS,
    Permission.MANAGE_PORTFOLIO,
    
    // Version Control
    Permission.CREATE_RELEASES,
    
    // API Integration
    Permission.ADD_INTEGRATIONS,
    Permission.MANAGE_VENDORS,
    
    // Deployment
    Permission.DEPLOY_TO_PRODUCTION,
    Permission.MANAGE_INFRASTRUCTURE,
    
    // Compliance
    Permission.VIEW_COMPLIANCE,
    Permission.MODIFY_COMPLIANCE,
    Permission.AUDIT_ACCESS,
    Permission.GENERATE_REPORTS,
    
    // Analytics
    Permission.VIEW_DEPARTMENT_ANALYTICS,
    Permission.VIEW_PROJECT_ANALYTICS,
    Permission.VIEW_TEAM_ANALYTICS,
    
    // Resources
    Permission.CREATE_TEMPLATES,
    Permission.DEFINE_STANDARDS,
    Permission.MANAGE_TRAINING
  ],
  
  [UserRole.SUPER_ADMIN]: [
    // All permissions
    ...Object.values(Permission)
  ],

  [UserRole.ADMIN]: [
    // All permissions
    ...Object.values(Permission)
  ]
}

// Helper Functions
export function hasPermission(role: UserRole, permission: Permission): boolean {
  return rolePermissions[role]?.includes(permission) || false
}

export function hasAnyPermission(role: UserRole, permissions: Permission[]): boolean {
  return permissions.some(permission => hasPermission(role, permission))
}

export function hasAllPermissions(role: UserRole, permissions: Permission[]): boolean {
  return permissions.every(permission => hasPermission(role, permission))
}

export function getRoleDisplayName(role: UserRole): string {
  const displayNames: Record<UserRole, string> = {
    [UserRole.DEVELOPER]: 'Developer',
    [UserRole.TEAM_LEAD]: 'Team Lead',
    [UserRole.PROJECT_MANAGER]: 'Project Manager',
    [UserRole.QA_REVIEWER]: 'QA/Reviewer',
    [UserRole.DEPARTMENT_HEAD]: 'Department Head',
    [UserRole.SUPER_ADMIN]: 'Super Admin',
    [UserRole.ADMIN]: 'Admin'
  }
  return displayNames[role] || role
}

export function getRoleDescription(role: UserRole): string {
  const descriptions: Record<UserRole, string> = {
    [UserRole.DEVELOPER]: 'Individual contributor focused on code development and implementation',
    [UserRole.TEAM_LEAD]: 'Technical leader managing development team and code quality',
    [UserRole.PROJECT_MANAGER]: 'Project oversight, planning, and resource management',
    [UserRole.QA_REVIEWER]: 'Quality assurance, testing, and compliance verification',
    [UserRole.DEPARTMENT_HEAD]: 'Strategic department management and governance',
    [UserRole.SUPER_ADMIN]: 'Full platform administration and system management',
    [UserRole.ADMIN]: 'Admin'
  }
  return descriptions[role] || ''
}

export function getRoleBadgeColor(role: UserRole): string {
  const colors: Record<UserRole, string> = {
    [UserRole.DEVELOPER]: 'bg-blue-100 text-blue-800',
    [UserRole.TEAM_LEAD]: 'bg-purple-100 text-purple-800',
    [UserRole.PROJECT_MANAGER]: 'bg-green-100 text-green-800',
    [UserRole.QA_REVIEWER]: 'bg-yellow-100 text-yellow-800',
    [UserRole.DEPARTMENT_HEAD]: 'bg-orange-100 text-orange-800',
    [UserRole.SUPER_ADMIN]: 'bg-red-100 text-red-800',
    [UserRole.ADMIN]: 'bg-red-100 text-red-800'
  }
  return colors[role] || 'bg-gray-100 text-gray-800'
}